import React, { useCallback, useState, useContext, useRef } from 'react';
import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import {
  Container,
  Paper,
  Box,
  Typography,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  Alert,
  AlertTitle,
  Autocomplete,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link
} from '@mui/material';
import { colors } from 'src/components/App/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { ContentPageTitle } from 'src/components/ContentPageTitle/ContentPageTitle';
import { CPRFormData } from './types';
import { clamp } from 'lodash';
import { endOfDay, format } from 'date-fns';
import { InlineBox } from '../../Layout/InlineBox';
import {
  AttType,
  CprActivityStatus,
  useUpsertCprActivityAsyncMutation,
  useTransitionCprActivityAsyncMutation,
  useGetActivityTagDetailAsyncQuery
} from 'src/graphql/generated';
import { useCprAttachmentsUpload } from './Upload/useCprAttachmentsUpload';
import { Attachments } from 'src/components/Attachments/Attachments';
import { useMemberActivity } from './useMemberActivity';
import { AttachmentsArray } from './types';
import { CPRFormContent } from './CPRFormContent';
type CPRProps = {
  activityKeyFromUrlOrCpdId?: string;
};

const CPRForm = ({ activityKeyFromUrlOrCpdId }: CPRProps) => {
  const editing = !!activityKeyFromUrlOrCpdId;
  const { activeMemberId } = useContext(AuthContext) as AuthContextType;
  const { canImpersonate } = useContext(AuthContext) as AuthContextType;
  const { cpdRole } = useContext(AuthContext) as AuthContextType;
  const [getActivityKey, setGetActivityKey] = useState<string | null | undefined>('');
  const [open, setOpen] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cprStatus, setCprStatus] = useState<CprActivityStatus>();
  const [submitting, setSubmitting] = useState(false);
  const [actionAlert, setActionAlert] = useState({
    title: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const { memberActivityData, loading } = useMemberActivity({
    activityKey: activityKeyFromUrlOrCpdId,
    memberId: activeMemberId,
    skip: editing
  });
  const scrollRef = useRef<HTMLElement>(null);
  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 10);
  }, []);

  const currentCprStatus = useCallback(
    (serverStatus: CprActivityStatus | null | undefined, fallback?: CprActivityStatus) => {
      setCprStatus(serverStatus ?? fallback);
    },
    []
  );

  const plrOptions = [
    {
      value: 'EP',
      label: 'Professionalism and Ethical Practice',
      tooltip:
        'Doctors have a duty to make the care of patients their first concern and practice medicine safely and effectively'
    }
  ];
  const { register, handleSubmit, control, watch, setValue, formState, reset, getValues, trigger } =
    useForm<CPRFormData>({
      defaultValues: {
        activityKey: activityKeyFromUrlOrCpdId || '',
        activityTitle: '',
        activityDate: new Date(),
        eaHours: 0,
        rpHours: 0,
        hours: 0,
        cprAttachements: [],
        agreedToDeclaration: false,
        pcComments: '',
        pcDueDate: new Date(),
        pcNextActionDate: new Date(),
        program_levels: [],
        pcNotes: '',
        pcTags: [],
        cprPcAttachments: []
      },
      mode: 'onChange'
    });

  let maxDate = endOfDay(new Date());
  const maxDatePCFields = new Date(maxDate.getFullYear() + 1, 0, 0, 23, 59, 59, 999);

  let minAllowedDate: Date;
  if (cpdRole === 'staff') {
    minAllowedDate = new Date('01/02/2023');
  } else if (cpdRole === 'member') {
    minAllowedDate = new Date('01/02/2026');
  }
  const watchAllFields = watch();
  const nanToZero = (num: number) => {
    if (Number.isNaN(num)) {
      return 0;
    } else return num;
  };
  const roundHalf = (num: number) => {
    return Math.round(num * 2) / 2;
  };
  const roundField = useCallback(
    (fieldName: 'eaHours' | 'rpHours' | 'hours') => {
      setValue(fieldName, clamp(roundHalf(watch(fieldName)), 0, 500), {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, watch]
  );

  const hasCPDHoursGreaterThanZero = watchAllFields.eaHours > 0 || watchAllFields.rpHours > 0;

  const totalCPDHours = parseFloat(
    (nanToZero(Number(watchAllFields.eaHours)) + nanToZero(Number(watchAllFields.rpHours))).toFixed(
      1
    )
  );

  const { data: dataPcTagsList, error: errorPcTags } = useGetActivityTagDetailAsyncQuery({});
  const tagsListStaff = dataPcTagsList?.getActivityTagDetailAsync;
  if (errorPcTags) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Something went wrong please try again</Alert>
      </Box>
    );
  }
  const [upsertCprActivityMutation] = useUpsertCprActivityAsyncMutation();
  const [transitionCprActivityMutation] = useTransitionCprActivityAsyncMutation();
  const { processAttachments } = useCprAttachmentsUpload();
  type ActionType = 'SAVE' | 'SUBMIT' | 'CANCEL';
  const [action, setAction] = useState<ActionType>('SUBMIT');
  const handleUpsert = async (
    formInputdata: CPRFormData,
    action: ActionType,
    setSubmitting?: (value: boolean) => void
  ) => {
    let status;
    switch (action) {
      case 'SUBMIT':
        status = CprActivityStatus.CprSubmitted;
        setActionAlert({
          title: 'Submitted',
          message:
            'Your CPR submission is being processed and will appear in your CPD statement within 5 minutes'
        });
        break;

      case 'CANCEL':
        status = CprActivityStatus.CprCancelled;
        setActionAlert({
          title: 'Cancelled',
          message: 'Your CPR activity has been cancelled.'
        });
        break;

      case 'SAVE':
      default:
        status = cprStatus ?? CprActivityStatus.CprDraft;
        break;
    }
    try {
      setSnackbar((prev) => ({ ...prev, open: false }));
      const selectedTagList = formInputdata?.pcTags;
      const uploadResponse = await processAttachments(formInputdata);
      if (!uploadResponse) {
        setSnackbar({
          open: true,
          message: 'Failed to upload files, please try again later.',
          severity: 'error'
        });
        return;
      }
      const { aggregatedCprAttachments, aggregatedPcAttachments } = uploadResponse;

      const upsertInputVariables = {
        action: status as CprActivityStatus,
        memberId: activeMemberId,
        date: formInputdata?.activityDate?.toISOString(),
        hours: totalCPDHours,
        activityKey: getActivityKey ? getActivityKey! : '',
        title: formInputdata?.activityTitle,
        cprAttachements: aggregatedCprAttachments,
        cprPcAttachments: aggregatedPcAttachments,
        cpdTypes: [
          { type: 'RP', hours: formInputdata?.rpHours },
          { type: 'EA', hours: formInputdata?.eaHours }
        ],
        pcComments: formInputdata?.pcComments,
        pcDueDate: formInputdata?.pcDueDate?.toISOString(),
        pcNextActionDate: formInputdata?.pcNextActionDate?.toISOString(),
        pcNotes: formInputdata?.pcNotes,
        pcTags: selectedTagList,
        program_levels: formInputdata?.program_levels,
        agreedToDeclaration: formInputdata?.agreedToDeclaration
      };
      await upsertCprActivityMutation({
        variables: upsertInputVariables,
        onCompleted: async (data) => {
          setGetActivityKey(data?.upsertCPRActivityAsync?.activitykey);
          currentCprStatus(
            data?.upsertCPRActivityAsync?.status as CprActivityStatus,
            status as CprActivityStatus
          );
          setSnackbar({
            open: true,
            message: 'Form saved.',
            severity: 'success'
          });
          if (action != 'SAVE' && data?.upsertCPRActivityAsync?.activitykey) {
            await transitionCprActivityMutation({
              variables: {
                action: status as CprActivityStatus,
                memberid: activeMemberId,
                activitykey: data?.upsertCPRActivityAsync?.activitykey
              },
              onCompleted: (data) => {
                scrollToTop();
                setOpen(true);
                currentCprStatus(
                  data?.TransitionCPRActivityAsync?.status as CprActivityStatus,
                  status as CprActivityStatus
                );
              }
            });
          }
          if (setSubmitting) {
            setSubmitting && setSubmitting(false);
          }
        },
        onError: () => {
          setSubmitting && setSubmitting(false);
        }
      });
    } catch (error: any) {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }
  };

  const handleSave = async () => {
    const isValid = await trigger('activityTitle', { shouldFocus: true });
    if (!isValid) return;
    const formData = getValues();
    await handleUpsert(formData, 'SAVE');
  };
  const handleCancelOpen = () => {
    setOpenCancelDialog(true);
  };
  const handleCancelClose = () => {
    setOpenCancelDialog(false);
  };
  const handleCancelConfirm = async () => {
    const formData = getValues();
    await handleUpsert(formData, 'CANCEL');
    setOpenCancelDialog(false);
  };

  const onSubmit = async (data: CPRFormData) => {
    if (submitting) return;
    setSubmitting(true);
    await handleUpsert(data, action, setSubmitting);
  };
  React.useEffect(() => {
    const editFormData = memberActivityData?.getCPRMemberActivitiesInProgressByKey;
    if (!editing) return;
    if (!editFormData) return;
    const selectedPcTags = editFormData?.pctags?.filter(Boolean) ?? [];
    setGetActivityKey(activityKeyFromUrlOrCpdId);
    if (editFormData?.status) {
      setCprStatus(editFormData?.status as CprActivityStatus);
    }
    //reset form to member activity data when it's available, so isDirty will be false after reset
    reset({
      activityKey: activityKeyFromUrlOrCpdId,
      activityTitle: editFormData?.title || '',
      activityDate: editFormData?.date ? new Date(editFormData?.date) : null,
      eaHours: editFormData?.cpd_types?.find((cpdType) => cpdType?.type === 'EA')?.hours || 0,
      rpHours: editFormData?.cpd_types?.find((cpdType) => cpdType?.type === 'RP')?.hours || 0,
      hours: editFormData?.hours,
      program_levels: editFormData?.program_levels as string[],
      cprAttachements:
        (editFormData?.attachments?.filter(
          (a) => a?.attachment_type === AttType.Cprattachments
        ) as AttachmentsArray) || [],
      cprPcAttachments:
        (editFormData?.attachments?.filter(
          (a) => a?.attachment_type === AttType.Cprpcattachments
        ) as AttachmentsArray) || [],
      pcComments: editFormData?.pccomments || '',
      pcNotes: editFormData?.pcnotes || '',
      pcDueDate: editFormData?.pcduedate ? new Date(editFormData?.pcduedate) : null,
      pcNextActionDate: editFormData?.pcnextactiondate
        ? new Date(editFormData?.pcnextactiondate)
        : null,
      pcTags: selectedPcTags as string[],
      agreedToDeclaration: editFormData?.agreedtoDeclaration
    });
  }, [editing, memberActivityData, reset]);

  const { isValid } = formState;

  const handleCloseSnack = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  if (editing && loading) return <Container maxWidth="lg">Loading...</Container>;
  const canGPEdit = [null, undefined, CprActivityStatus.CprDraft];
  const gpCanCancelStatuses = [CprActivityStatus.CprDraft];
  const pcCanCancelStatuses = [CprActivityStatus.CprDraft];
  const gpHideSaveStatuses = [CprActivityStatus.CprSubmitted, CprActivityStatus.CprCancelled];
  const pcCanApproveSubmitStatuses = [CprActivityStatus.CprSubmitted];

  const isFormDisabled = !canImpersonate && !canGPEdit.includes(cprStatus as CprActivityStatus);

  const showCancelButton = canImpersonate
    ? pcCanCancelStatuses.includes(cprStatus as CprActivityStatus)
    : gpCanCancelStatuses.includes(cprStatus as CprActivityStatus);

  const isSubmitDisabled = canImpersonate
    ? !pcCanApproveSubmitStatuses.includes(cprStatus as CprActivityStatus)
    : !gpHideSaveStatuses.includes(cprStatus as CprActivityStatus);

  const isSaveHidden =
    !canImpersonate && gpHideSaveStatuses.includes(cprStatus as CprActivityStatus);

  return (
    <>
      <ContentPageTitle
        currentPageTitle="Cardiopulmonary Resuscitation (CPR)"
        showImpersonate={true}
      />
      <Container maxWidth="lg">
        {open && (
          <Box ref={scrollRef}>
            <Box sx={{ my: 2, p: 3, pb: 2, bgcolor: colors['green.50'] }}>
              <Box width={'100%'} flexGrow={1}>
                <AlertTitle>
                  <Typography component={'span'} whiteSpace={'pre-wrap'} variant={'body1'}>
                    <InlineBox fontWeight={600}>
                      {actionAlert.title}
                      {'\t'}
                    </InlineBox>
                  </Typography>
                </AlertTitle>
                <Typography width={'100%'} variant={'body2'}>
                  {actionAlert.message}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <Paper sx={{ p: 3, my: 2 }} elevation={3}>
          <CPRFormContent />
          <form onSubmit={handleSubmit(onSubmit)}>
            <Divider sx={{ mt: 4, mb: 2 }} />
            {canImpersonate && (
              <>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Due Date
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 3, width: '340px', maxWidth: '100%' }}>
                      <Controller
                        control={control}
                        name={'pcDueDate'}
                        render={({ fieldState: { error }, field: { value, ref } }) => (
                          <DatePicker
                            openTo="year"
                            ref={ref}
                            inputFormat={'dd/MM/yyyy'}
                            views={['day']}
                            value={value || null}
                            onChange={(e) => {
                              setValue('pcDueDate', e, {
                                shouldValidate: true,
                                shouldDirty: true
                              });
                            }}
                            minDate={minAllowedDate}
                            maxDate={maxDatePCFields}
                            renderInput={(params) => (
                              <TextField
                                sx={{ width: '100%' }}
                                {...params}
                                label="Select Date"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Next Action Date
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 3, width: '340px', maxWidth: '100%' }}>
                      <Controller
                        control={control}
                        name={'pcNextActionDate'}
                        render={({ fieldState: { error }, field: { value, ref } }) => (
                          <DatePicker
                            openTo="year"
                            ref={ref}
                            inputFormat={'dd/MM/yyyy'}
                            views={['day']}
                            value={value || null}
                            onChange={(e) => {
                              setValue('pcNextActionDate', e, {
                                shouldValidate: true,
                                shouldDirty: true
                              });
                            }}
                            minDate={minAllowedDate}
                            maxDate={maxDatePCFields}
                            renderInput={(params) => (
                              <TextField
                                sx={{ width: '100%' }}
                                {...params}
                                label="Select Date"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Tags
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ mb: 3, Width: '100%' }}>
                      <Controller
                        name="pcTags"
                        control={control}
                        render={({ field }) => {
                          const displayValue =
                            field.value
                              ?.map((code) =>
                                tagsListStaff?.find((opt) => opt?.optionCode === code)
                              )
                              .filter(Boolean) ?? [];

                          return (
                            <Autocomplete
                              multiple
                              limitTags={2}
                              id="pcTags"
                              options={tagsListStaff ?? []}
                              value={displayValue}
                              onChange={(_, newValue) => {
                                const codes = newValue
                                  .map((opt) => opt?.optionCode)
                                  .filter((code): code is string => !!code && code.length > 0);
                                field.onChange(codes);
                              }}
                              getOptionLabel={(option) => option?.optionText ?? ''}
                              isOptionEqualToValue={(option, value) => {
                                if (typeof value === 'string') {
                                  return option?.optionCode === value;
                                }
                                return option?.optionCode === value?.optionCode;
                              }}
                              renderInput={(params) => (
                                <TextField {...params} label="Select Tags" />
                              )}
                            />
                          );
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Notes
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8} sx={{ mb: 3 }}>
                    <Box sx={{ width: '100%', mt: 0 }}>
                      <Controller
                        name="pcNotes"
                        control={control}
                        rules={{
                          maxLength: 100,
                          required: false
                        }}
                        render={({ field }) => (
                          <TextField
                            label="For internal use only"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            multiline
                            rows={3}
                            sx={{ m: 0 }}
                            {...field}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Comments
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ width: '100%', mt: 0 }}>
                      <Controller
                        name="pcComments"
                        control={control}
                        rules={{
                          maxLength: 100,
                          required: false
                        }}
                        render={({ field }) => (
                          <TextField
                            label="From your program coordinator"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            multiline
                            rows={3}
                            sx={{ mb: 2, mt: 0 }}
                            {...field}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Attachments{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Controller
                      control={control}
                      name={'cprPcAttachments'}
                      rules={{ max: 5, required: false }}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <Attachments
                            onChangeAttachments={onChange}
                            attachments={value}
                            uploadText="FILE"
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 4, mb: 2 }} />
              </>
            )}
            <Grid container>
              <fieldset
                disabled={isFormDisabled}
                style={{
                  minWidth: 0,
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  pointerEvents: isFormDisabled ? 'none' : 'auto'
                }}
              >
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Activity Details{' '}
                      <Typography variant={'caption'} fontWeight={400}>
                        (Required*)
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box>
                      <Typography sx={{ color: colors['charcoal.700'] }} variant={'body2'}>
                        Examples of activities you can record on this form include:
                      </Typography>
                      <List
                        sx={{
                          color: colors['charcoal.700'],
                          listStyleType: 'disc',
                          width: '100%',
                          listStylePosition: 'outside',
                          pl: 3,
                          mb: 1
                        }}
                      >
                        <ListItem sx={{ display: 'list-item', p: 0 }}>
                          <Typography sx={{ color: colors['charcoal.700'] }} variant={'body2'}>
                            HLTAID009: Provide cardiopulmonary resuscitation
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item', p: 0 }}>
                          <Typography sx={{ color: colors['charcoal.700'] }} variant={'body2'}>
                            CPR
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item', p: 0 }}>
                          <Typography sx={{ color: colors['charcoal.700'] }} variant={'body2'}>
                            Basic life support (BLS)
                          </Typography>
                        </ListItem>
                      </List>
                      <Box sx={{ mb: 2 }}>
                        <Controller
                          name="activityTitle"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              {...field}
                              {...register('activityTitle', { required: true, minLength: 3 })}
                              error={!!error}
                              fullWidth={true}
                              id="activity_title"
                              label="Activity title"
                              multiline
                              inputProps={{ maxLength: 100 }}
                              variant="outlined"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ mb: 3, width: '340px', maxWidth: '100%' }}>
                        <Controller
                          control={control}
                          name={'activityDate'}
                          rules={{
                            required: true,
                            validate: {
                              required: (value) => !!value,
                              activityDateAllowed: (value) => {
                                if (!value) return;
                                const isactivityDateAllowed = new Date(value) >= minAllowedDate;
                                if (!isactivityDateAllowed) {
                                  return (
                                    'Activity date must be on or after ' +
                                    format(minAllowedDate, 'dd/MM/yyyy') +
                                    '.'
                                  );
                                }
                              },
                              isPast: (value) => {
                                if (!value) return;
                                const isPast = new Date(value) <= maxDate;
                                if (!isPast) {
                                  return `Activity date must be on or before ${format(
                                    new Date(),
                                    'dd/MM/yyyy'
                                  )}.`;
                                }
                              }
                            }
                          }}
                          render={({ fieldState: { error }, field: { value, ref } }) => (
                            <DatePicker
                              openTo="year"
                              ref={ref}
                              inputFormat={'dd/MM/yyyy'}
                              views={['day']}
                              value={value || null}
                              onChange={(e) => {
                                setValue('activityDate', e, {
                                  shouldValidate: true,
                                  shouldDirty: true
                                });
                              }}
                              minDate={minAllowedDate}
                              maxDate={maxDate}
                              renderInput={(params) => (
                                <TextField
                                  sx={{ width: '100%' }}
                                  {...params}
                                  label="Activity completion date"
                                  error={!!error}
                                  helperText={error?.message}
                                />
                              )}
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ width: '340px', maxWidth: '100%' }}>
                        <Typography variant="body1" color={'primary'} sx={{ display: 'flex' }}>
                          Educational Activities
                          <Tooltip title="Educational Activities (EA) are activities that expand your general practice knowledge, skills, and attitudes, related to your scope of practice">
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{ ml: 1, maxWidth: 20, maxHeight: 20 }}
                            />
                          </Tooltip>
                        </Typography>
                        <Box sx={{ display: 'flex', mt: 2, mb: 3 }}>
                          <Controller
                            name="eaHours"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                {...register('eaHours', {
                                  setValueAs: (v: any) => nanToZero(Number.parseFloat(v)),
                                  onBlur: () => {
                                    roundField('eaHours');
                                  }
                                })}
                                sx={{ width: '100%' }}
                                type={'number'}
                                label="Hours"
                                variant="outlined"
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 500,
                                    step: 0.5
                                  }
                                }}
                              />
                            )}
                          />
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <Typography variant="body1" sx={{ fontWeight: '700' }}>
                            Total:
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              marginLeft: 'auto',
                              flexDirection: 'column',
                              alignItems: 'flex-end'
                            }}
                          >
                            <Typography sx={{ fontWeight: '700' }} color={'secondary'}>
                              {totalCPDHours} CPD hours
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 4, mb: 2 }} />
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 0 }}>
                      Program-level requirements{' '}
                      <Typography variant={'caption'} fontWeight={400}>
                        (Optional)
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography sx={{ color: colors['charcoal.700'], mb: 2 }} variant={'body2'}>
                      If this activity covers one or more program-level requirement (PLR), record it
                      here. Unsure which PLRs are applicable? See our{' '}
                      <Link
                        rel="noreferrer"
                        target="_blank"
                        href="https://www.racgp.org.au/cpd/cpd-home/join-racgp-home/cpd-program-level-requirements"
                      >
                        PLRs page. 
                      </Link>
                    </Typography>
                    <FormGroup sx={{ alignItems: 'flex-start' }}>
                      {plrOptions.map((plrOption: any, index) => (
                        <Box display="flex" alignItems="center" gap={1} key={index}>
                          <Controller
                            name="program_levels"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{ mr: 0 }}
                                control={
                                  <Checkbox
                                    value={plrOption.value}
                                    checked={field.value?.includes(plrOption?.value) || false}
                                    onChange={() => {
                                      const plrSelectedOptions = field.value?.includes(
                                        plrOption?.value
                                      )
                                        ? field.value.filter((v) => v !== plrOption.value)
                                        : [...field.value, plrOption.value];
                                      field.onChange(plrSelectedOptions);
                                    }}
                                  />
                                }
                                label={plrOption.label}
                              />
                            )}
                          />
                          <Tooltip title={plrOption.tooltip}>
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{ ml: 1, maxWidth: 20, maxHeight: 20 }}
                            />
                          </Tooltip>
                        </Box>
                      ))}
                    </FormGroup>
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 4, mb: 2 }} />
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Evidence of CPR{' '}
                      <Typography variant={'caption'} fontWeight={400}>
                        (Required*)
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Typography sx={{ mb: 2, color: colors['charcoal.700'] }} variant={'body2'}>
                      Once this activity is approved, the triennium CPR requirement will be marked
                      as complete for specialist GPs.
                    </Typography>
                    <Typography sx={{ mb: 2, color: colors['charcoal.700'] }} variant={'body2'}>
                      <InlineBox fontWeight={600}>Note: </InlineBox> If the name on your certificate
                      differs from your RACGP membership record, we may request additional evidence
                      which can also be uploaded here (eg marriage certificate, change of name
                      certificate).
                    </Typography>
                    <Controller
                      control={control}
                      name={'cprAttachements'}
                      rules={{ max: 5, required: true }}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <Attachments
                            onChangeAttachments={onChange}
                            attachments={value}
                            uploadText="EVIDENCE"
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 4, mb: 2 }} />
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Declaration{' '}
                      <Typography variant={'caption'} fontWeight={400}>
                        (Required*)
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography sx={{ mb: 2 }}>I declare the following:</Typography>
                    <List
                      sx={{
                        listStyleType: 'disc',
                        width: '100%',
                        listStylePosition: 'outside',
                        pl: 4
                      }}
                    >
                      <ListItem sx={{ display: 'list-item', p: 0 }}>
                        <Typography sx={{ mb: 0 }}>I acknowledge that:</Typography>
                        <List
                          sx={{
                            listStyleType: 'circle',
                            width: '100%',
                            listStylePosition: 'outside',
                            pl: 3
                          }}
                        >
                          <ListItem sx={{ display: 'list-item', p: 0 }}>
                            RACGP may contact me directly to obtain further information relating to
                            this activity for the purposes of undertaking a Quality Assurance
                            Assessment;
                          </ListItem>
                          <ListItem sx={{ display: 'list-item', p: 0 }}>
                            RACGP may disclose the information contained in this CPR form to Ahpra
                            and the Medical Board of Australia; and
                          </ListItem>
                          <ListItem sx={{ display: 'list-item', p: 0 }}>
                            RACGP reserves the right to amend my CPD record if this activity does
                            not meet relevant Quality Assurance Assessment requirements.
                          </ListItem>
                        </List>
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', p: 0 }}>
                        <Typography sx={{ mb: 2 }}>
                          The information I have provided in this CPR form is true and accurate.
                        </Typography>
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', p: 0 }}>
                        <Typography sx={{ mb: 2 }}>
                          I acknowledge that providing false or misleading information in this CPR
                          form may constitute Academic Misconduct within the meaning of the RACGP
                          Academic Misconduct Policy.
                        </Typography>
                      </ListItem>
                    </List>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Controller
                        name="agreedToDeclaration"
                        control={control}
                        rules={{ required: cpdRole === 'staff' ? false : true }}
                        defaultValue={false}
                        render={({ field: { value, onChange, ...field } }) => (
                          <Checkbox
                            {...field}
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                            disabled={cpdRole === 'staff'}
                          />
                        )}
                      />
                      <Typography sx={{ color: '#6f6f6f' }}>I agree</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 4, mb: 2 }} />
              </fieldset>
            </Grid>
            <Grid container>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'flex-start'
                  }}
                >
                  {showCancelButton && (
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{
                        minWidth: '180px',
                        ml: { xs: 0, md: 2 },
                        mt: { xs: 2, sm: 0 },
                        mb: { xs: 2, md: 0 }
                      }}
                      type="button"
                      onClick={handleCancelOpen}
                    >
                      Cancel Activity
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'flex-end'
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: '180px',
                      mr: { xs: 0, sm: 2 },
                      mb: { xs: 2, sm: 0 },
                      display: isSaveHidden ? 'none' : 'block'
                    }}
                    type="button"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                  {isSubmitDisabled && (
                    <Button
                      variant="contained"
                      sx={{
                        minWidth: '180px'
                      }}
                      type="submit"
                      disabled={!isValid || !hasCPDHoursGreaterThanZero || submitting}
                      onClick={() => setAction('SUBMIT')}
                    >
                      Submit
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={handleCloseSnack}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnack} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>

        <Dialog open={openCancelDialog} onClose={handleCancelClose}>
          <DialogTitle>Cancel Activity</DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure you want to delete this Activity?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelConfirm} color="primary">
              Delete
            </Button>
            <Button onClick={handleCancelClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};
export { CPRForm };
